import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
    snapshotBox: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    snapshotRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
    },
    snapshotDetail: {
        fontFamily: "monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    logList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        minHeight: 200,
    },
    logEntry: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(0.5, 1),
        fontSize: 12,
        "& summary": {
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            flexWrap: "wrap",
            listStyle: "none",
            outline: "none",
        },
    },
    timestamp: {
        fontFamily: "monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
    },
    statusTransition: {
        fontFamily: "monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
        wordBreak: "break-all",
    },
    jsonBlock: {
        background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f6fa",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        padding: theme.spacing(1),
        margin: theme.spacing(0.5, 0),
        fontSize: 11,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 260,
        overflowY: "auto",
    },
    jsonLabel: {
        fontWeight: 600,
        fontSize: 11,
        marginTop: theme.spacing(0.5),
    },
    emptyState: {
        textAlign: "center",
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
    chipError: {
        background: "#c62828",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipSuccess: {
        background: "#2e7d32",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipWarning: {
        background: "#ef6c00",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipNeutral: {
        height: 20,
        fontSize: 10,
    },
    loadMoreBox: {
        textAlign: "center",
        padding: theme.spacing(1),
    },
}));

const ERROR_EVENTS_PREFIXES = ["ERROR_", "RECONNECT_FAILED", "CIRCUIT_BREAKER_ACTIVATED", "DELETED", "KEEP_ALIVE_ERROR"];
const SUCCESS_EVENTS = ["CONNECTED", "RECONNECT_SUCCESS", "QR_SCANNED", "HEALTH_CHECK_OK", "CIRCUIT_BREAKER_DEACTIVATED"];

const eventChipClass = (classes, event) => {
    if (!event) return classes.chipNeutral;
    if (ERROR_EVENTS_PREFIXES.some(p => event.startsWith(p))) return classes.chipError;
    if (SUCCESS_EVENTS.includes(event)) return classes.chipSuccess;
    if (event === "DISCONNECTED" || event === "MANUAL_DISCONNECT" || event === "HEALTH_CHECK_DEGRADED") return classes.chipWarning;
    return classes.chipNeutral;
};

const sessionStateChipClass = (classes, state) => {
    if (state === "CONNECTED") return classes.chipSuccess;
    if (state === "DEGRADED" || state === "RECONNECTING") return classes.chipWarning;
    return classes.chipError; // DISCONNECTED | BLOCKED
};

const formatTime = (ts) => {
    if (!ts) return "-";
    // MySQL pode devolver "2026-06-30 07:51:56" (sem o "T"), que o Safari não parseia
    const normalized = typeof ts === "string" ? ts.replace(" ", "T") : ts;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(ts);
    return date.toLocaleString("pt-BR");
};

const ConnectionLogsModal = ({ open, onClose, whatsAppId, whatsAppName }) => {
    const classes = useStyles();

    const [logs, setLogs] = useState([]);
    const [count, setCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hideQr, setHideQr] = useState(true);

    const fetchLogs = useCallback(async (page = 1, append = false) => {
        if (!whatsAppId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/whatsapp/${whatsAppId}/connection-logs`, {
                params: {
                    pageNumber: page,
                    excludeEvents: hideQr ? "QR_GENERATED" : undefined,
                },
            });
            setLogs(prev => (append ? [...prev, ...(data.logs || [])] : data.logs || []));
            setCount(data.count || 0);
            setHasMore(!!data.hasMore);
            setSnapshot(data.snapshot || null);
            setPageNumber(page);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    }, [whatsAppId, hideQr]);

    useEffect(() => {
        if (open) {
            fetchLogs(1, false);
        } else {
            setLogs([]);
            setCount(0);
            setHasMore(false);
            setPageNumber(1);
            setSnapshot(null);
        }
    }, [open, fetchLogs]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>
                {i18n.t("connectionLogs.title", { name: whatsAppName || `#${whatsAppId}` })}
                <Typography variant="caption" display="block" color="textSecondary">
                    {i18n.t("connectionLogs.subtitle")}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {snapshot && (
                    <Box className={classes.snapshotBox}>
                        <Box className={classes.snapshotRow}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>{i18n.t("connectionLogs.currentState")}</Typography>
                            <Chip size="small" label={i18n.t("connectionLogs.chips.database", { value: snapshot.whatsapp?.status || "-" })} className={classes.chipNeutral} variant="outlined" />
                            <Chip
                                size="small"
                                label={i18n.t("connectionLogs.chips.memory", { value: snapshot.sessionState || "-" })}
                                className={sessionStateChipClass(classes, snapshot.sessionState)}
                            />
                            <Chip size="small" variant="outlined" label={`retries: ${snapshot.whatsapp?.retries ?? "-"}`} className={classes.chipNeutral} />
                            <Chip size="small" variant="outlined" label={i18n.t("connectionLogs.chips.reconnections", { value: snapshot.whatsapp?.reconnectAttempts ?? "-" })} className={classes.chipNeutral} />
                            {snapshot.whatsapp?.connectedNumber && (
                                <Chip size="small" variant="outlined" label={snapshot.whatsapp.connectedNumber} className={classes.chipNeutral} />
                            )}
                        </Box>
                        <Typography className={classes.snapshotDetail}>
                            {i18n.t("connectionLogs.timeline", {
                                lastReconnect: formatTime(snapshot.whatsapp?.lastReconnectAt),
                                nextRetry: formatTime(snapshot.whatsapp?.nextRetryAt),
                                lastHealthCheck: formatTime(snapshot.whatsapp?.lastHealthCheckAt),
                            })}
                        </Typography>
                        <Typography className={classes.snapshotDetail}>
                            {i18n.t("connectionLogs.scheduler", {
                                queued: snapshot.reconnectScheduler?.queued ?? "-",
                                generatedAt: formatTime(snapshot.generatedAt),
                            })}
                        </Typography>
                    </Box>
                )}
                <Box className={classes.snapshotRow} mb={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={hideQr}
                                onChange={e => setHideQr(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={<Typography variant="caption">{i18n.t("connectionLogs.hideQr")}</Typography>}
                    />
                    <Typography variant="caption" color="textSecondary">
                        {i18n.t("connectionLogs.totalEvents", { count })}
                    </Typography>
                </Box>
                <Box className={classes.logList}>
                    {!loading && logs.length === 0 && (
                        <Box className={classes.emptyState}>
                            <Typography variant="body2">{i18n.t("connectionLogs.empty")}</Typography>
                        </Box>
                    )}
                    {logs.map(log => (
                        <details key={log.id} className={classes.logEntry}>
                            <summary>
                                <span className={classes.timestamp}>{formatTime(log.createdAt)}</span>
                                <Chip size="small" label={log.event} className={eventChipClass(classes, log.event)} />
                                {(log.previousStatus || log.currentStatus) && (
                                    <span className={classes.statusTransition}>
                                        {log.previousStatus || "?"} → {log.currentStatus || "?"}
                                    </span>
                                )}
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={log.isAutomatic ? "auto" : "manual"}
                                    className={classes.chipNeutral}
                                />
                                {log.errorCode !== null && log.errorCode !== undefined && (
                                    <Chip size="small" label={i18n.t("connectionLogs.chips.code", { value: log.errorCode })} className={classes.chipError} />
                                )}
                            </summary>
                            {log.errorMessage && (
                                <>
                                    <Typography className={classes.jsonLabel} color="error">{i18n.t("common.error")}</Typography>
                                    <pre className={classes.jsonBlock}>{log.errorMessage}</pre>
                                </>
                            )}
                            {log.metadata && (
                                <>
                                    <Typography className={classes.jsonLabel}>Metadata</Typography>
                                    <pre className={classes.jsonBlock}>
                                        {typeof log.metadata === "string" ? log.metadata : JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                </>
                            )}
                            {(log.user?.name || log.ipAddress) && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                    {log.user?.name ? i18n.t("connectionLogs.user", { name: log.user.name }) : ""}
                                    {log.user?.name && log.ipAddress ? " | " : ""}
                                    {log.ipAddress ? `ip: ${log.ipAddress}` : ""}
                                </Typography>
                            )}
                        </details>
                    ))}
                    {loading && (
                        <Box textAlign="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading && hasMore && (
                        <Box className={classes.loadMoreBox}>
                            <Button size="small" variant="outlined" onClick={() => fetchLogs(pageNumber + 1, true)}>
                                {i18n.t("common.loadMore")}
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => fetchLogs(1, false)} color="default" variant="outlined" disabled={loading}>
                    {i18n.t("connectionLogs.reload")}
                </Button>
                <Button onClick={onClose} color="primary" variant="contained">
                    {i18n.t("common.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConnectionLogsModal;
